/**
 * @file     CardCtaProvider.tsx
 * @readme   ../CLAUDE.md
 * @purpose  Gives every listing card in a grid the CTA its surface calls for,
 *           and owns the one modal they share (alert signup or listing inquiry).
 *
 *           Takes children as a slot so the grid it wraps stays a server
 *           component. Wrapping the grid itself in a client component would
 *           pull twelve static SEO pages client-side, and this codebase has
 *           already paid for that once (0.468 field CLS on the homepage).
 *
 *           One modal for the whole grid, not one per card: a 24-card page
 *           would otherwise mount 24 dialogs.
 */
'use client';

import { Fragment, useCallback, useMemo, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useAnalytics } from '@/hooks/useAnalytics';
import { eventIntent } from '@/features/seo/shortlistIntent';
import { cardCtaFor, type CardCtaSurface } from '../cardCta';
import { CardCtaContext, type CardCtaHandle } from './CardCtaContext';

const CardCtaInquiryRoot = dynamic(() => import('./CardCtaInquiryRoot'));
// Loaded on the first CTA click, not with the grid: the signup flow is
// form-heavy and every static grid page would otherwise ship it up front.
const AlertSignupModal = dynamic(
  () => import('@/features/seo/AlertSignupModal').then((m) => m.AlertSignupModal),
  { ssr: false },
);

type Props = {
  surface: CardCtaSurface;
  /** Place the alert is scoped to, e.g. "Surrey". */
  city: string;
  /** Slug used for links and analytics. */
  slug: string;
  children: ReactNode;
};

export function CardCtaProvider({ surface, city, slug, children }: Props) {
  const cta = cardCtaFor(surface);
  const { trackCtaClick } = useAnalytics();
  const [open, setOpen] = useState(false);
  const [modalLoaded, setModalLoaded] = useState(false);
  // A visitor clicking the CTA on five cards should not create five leads.
  // The flow captures one email and one saved search per surface, so once it
  // has been completed here the button reports that state instead of
  // reopening a form that would submit again. Inquiry is listing-scoped, so
  // that latch must not apply there: asking about lot A is not asking about lot B.
  const [done, setDone] = useState(false);

  const onCardCta = useCallback(() => {
    if (!cta) return;
    // Reuses the existing event with a new location value. Event names are an
    // add-only protected surface, so nothing new is introduced here.
    trackCtaClick(cta.label, `card_cta_${surface}`, slug);
    if (cta.kind === 'alert') {
      setModalLoaded(true);
      setOpen(true);
    }
  }, [cta, slug, surface, trackCtaClick]);

  const handle = useMemo<CardCtaHandle | null>(() => {
    if (!cta) return null;
    if (cta.kind === 'inquiry') return null;
    if (cta.kind === 'link') {
      return {
        label: cta.label,
        href: cta.href(city, slug),
        onActivate: onCardCta,
        done: false,
        kind: 'link',
      };
    }
    return {
      label: done ? 'Watching' : cta.label,
      href: null,
      onActivate: done ? null : onCardCta,
      done,
      kind: 'alert',
    };
  }, [city, cta, done, onCardCta, slug]);

  if (cta?.kind === 'inquiry') {
    return (
      <CardCtaInquiryRoot surface={surface} slug={slug} label={cta.label}>
        {children}
      </CardCtaInquiryRoot>
    );
  }

  return (
    <CardCtaContext.Provider value={handle}>
      {/* Keyed so a server slot beside the modal does not trip the list-key warning. */}
      <Fragment key="slot">{children}</Fragment>
      {cta?.kind === 'alert' && modalLoaded && (
        <AlertSignupModal
          open={open}
          onClose={() => setOpen(false)}
          // Only an accepted subscription marks the surface done. Dismissing
          // the form must leave the CTA exactly as it was.
          onSent={() => setDone(true)}
          intent={eventIntent(cta.event, city)}
          locationTag={slug}
          place={city}
        />
      )}
    </CardCtaContext.Provider>
  );
}
